import { LocalizedError } from '@/lib/i18n/errors';
import type { OverviewReport, AgentReportRow, ChannelReportRow, CsatReport, InboxReport, SlaReport, TagsReport, BotsStats, ReportFilter } from './report-types';

export type ReportKind = 'overview' | 'agents' | 'channels' | 'csat' | 'inbox' | 'sla' | 'tags';
export interface ReportData {
  overview: OverviewReport; agents: AgentReportRow[]; channels: ChannelReportRow[];
  csat: CsatReport; inbox: InboxReport; sla: SlaReport; tags: TagsReport;
}
export interface ReportMeta {
  tenant_id: string; report: ReportKind; start_date: string; end_date: string;
  timezone: 'UTC'; updated_at: string; universe: 'authenticated tenant';
  time_unit: 'minutes'; percentage_unit: 'percent_0_100';
}
/** Cache the owned native receipt; observers select its projected data. */
export interface ReportRead<T> { data: T; meta: ReportMeta }
export interface BotStatsRead {
  data: BotsStats;
  meta: { tenant_id: string; scope: 'tenant_all_time'; updated_at: string; time_unit: 'seconds'; percentage_unit: 'percent_0_100' };
}
export class ReportReadError extends LocalizedError {
  constructor(key = 'error.read_failed', status?: number) { super('reports', key, {}, status); }
}
export class BotStatsReadError extends ReportReadError {
  constructor(key = 'read_failed', status?: number) { super('bots.error.' + key, status); }
}
export const reportUUID = (v: unknown): v is string => typeof v === 'string' && /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(v) && !/^0{8}(?:-0{4}){3}-0{12}$/.test(v);
const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const fail = (): never => { throw new ReportReadError('error.invalid_result'); };
type Reader<T = unknown> = (v: unknown, tenant: string) => T;
const literal: Reader<string> = v => {
  if (typeof v !== 'string' || v.length > 1_048_576 || v.includes('\0') || /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/u.test(v)) return fail();
  return v;
};
const number: Reader<number> = v => typeof v === 'number' && Number.isFinite(v) ? v : fail();
const positive: Reader<number> = (v, tenant) => { const n = number(v, tenant); return n >= 0 ? n : fail(); };
const count: Reader<number> = (v, tenant) => { const n = positive(v, tenant); return Number.isSafeInteger(n) ? n : fail(); };
const percent: Reader<number> = (v, tenant) => { const n = positive(v, tenant); return n <= 100 ? n : fail(); };
const ratingAverage: Reader<number> = (v, tenant) => { const n = positive(v, tenant); return n <= 10 ? n : fail(); };
const uuid: Reader<string> = v => reportUUID(v) ? v : fail();
const nullable = <T,>(reader: Reader<T>): Reader<T | null> => (v, tenant) => v === null ? null : reader(v, tenant);
const list = <T,>(reader: Reader<T>): Reader<T[]> => (v, tenant) => Array.isArray(v) && v.length <= 10000 ? v.map(row => reader(row, tenant)) : fail();
const optional = <T,>(reader: Reader<T>): Reader<T | undefined> => (v, tenant) => v === undefined ? undefined : reader(v, tenant);
function fields(spec: Record<string, Reader>): Reader<Record<string, unknown>> {
  return (v, tenant) => {
    if (!object(v) || v.tenant_id !== undefined && v.tenant_id !== tenant) return fail();
    return Object.fromEntries(Object.entries(spec).map(([key, reader]) => [key, reader(v[key], tenant)]).filter(([, value]) => value !== undefined));
  };
}
function numeric(keys: string[], reader: Reader = count) { return Object.fromEntries(keys.map(key => [key, reader])); }
function dateOnly(v: unknown): v is string {
  if (typeof v !== 'string' || !/^\d{4}-\d\d-\d\d$/.test(v)) return false;
  const [y, m, d] = v.split('-').map(Number), leap = y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0);
  return y >= 1 && m >= 1 && m <= 12 && d >= 1 && d <= [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];
}
const day: Reader<string> = v => dateOnly(v) ? v : fail();
const instant: Reader<string> = v => {
  if (typeof v !== 'string') return fail();
  const parts = /^(\d{4}-\d\d-\d\d)T(\d\d):(\d\d):(\d\d)(?:\.\d{1,9})?(Z|[+-](\d\d):(\d\d))$/.exec(v);
  if (!parts || !dateOnly(parts[1]) || Number(parts[2]) > 23 || Number(parts[3]) > 59 || Number(parts[4]) > 59 || Number(parts[6] ?? 0) > 23 || Number(parts[7] ?? 0) > 59 || !Number.isFinite(Date.parse(v))) return fail();
  return v;
};
const utc: Reader<string> = (v, tenant) => { const value = instant(v, tenant); return value.endsWith('Z') ? value : fail(); };
const hour: Reader<number> = (v, tenant) => { const n = count(v, tenant); return n <= 23 ? n : fail(); };
const dailyCount = fields({ date: day, count });
const bucket: Reader<{ rating: number; count: number }> = (v, tenant) => {
  const row = fields({ rating: count, count })(v, tenant);
  return Number(row.rating) >= 1 && Number(row.rating) <= 10 ? row as { rating: number; count: number } : fail();
};

const agent = fields({
  user_id: uuid, user_name: literal, user_email: literal,
  ...numeric(['conversations_assigned', 'conversations_resolved', 'current_open_load', 'messages_sent', 'messages_received', 'csat_responses']),
  avg_first_response_time: positive, avg_resolution_time: positive, avg_csat_rating: ratingAverage,
  last_activity_at: optional(instant),
});
const channel = fields({
  channel_id: uuid, channel_name: literal, provider_type: literal,
  ...numeric(['total_conversations', 'new_conversations', 'resolved_conversations', 'total_messages', 'inbound_messages', 'outbound_messages']),
  avg_first_response_time: positive, avg_resolution_time: positive, avg_csat_rating: ratingAverage,
  daily_volume: nullable(list(dailyCount)),
});
const overview = fields({
  ...numeric(['total_conversations', 'new_conversations', 'resolved_conversations', 'open_conversations', 'pending_conversations', 'total_messages', 'inbound_messages', 'outbound_messages', 'active_agents', 'csat_count', 'cohort_resolved_conversations']),
  avg_first_response_time: positive, avg_resolution_time: positive, avg_csat_rating: ratingAverage,
  csat_response_rate: percent, bot_resolution_rate: percent, cohort_resolution_rate: nullable(percent),
  closure_to_intake_ratio: nullable(positive), csat_scale_status: literal, csat_scale_max: nullable(count),
  daily: nullable(list(fields({ date: day, new: count, resolved: count }))),
  ...numeric(['conversations_trend', 'resolution_trend', 'csat_trend'], number),
});
const inbox = fields({
  by_status: nullable(list(fields({ status: literal, count }))),
  by_priority: nullable(list(fields({ priority: literal, count }))),
  by_channel: nullable(list(fields({ channel_id: uuid, channel_name: literal, provider_type: literal, count }))),
  daily_volume: nullable(list(dailyCount)), hourly_volume: nullable(list(fields({ hour, count }))),
  response_time_by_hour: nullable(list(fields({ hour, value: positive }))),
  avg_first_response_time: positive, avg_resolution_time: positive, total_conversations: count, total_messages: count,
});
const sla = fields({
  ...numeric(['first_response_total', 'first_response_met', 'first_response_breached', 'resolution_total', 'resolution_met', 'resolution_breached']),
  first_response_rate: percent, resolution_rate: percent, avg_first_response_time: positive, avg_resolution_time: positive,
  by_priority: nullable(list(fields({ priority: literal, target_first_response: count, target_resolution: count, first_response_rate: percent, resolution_rate: percent, total: count, met: count, breached: count }))),
  daily_trend: nullable(list(fields({ date: day, first_response_rate: percent, resolution_rate: percent, total: count }))),
});
const tagCounts: Reader<Record<string, number>> = (v, tenant) => {
  if (!object(v) || Object.keys(v).length > 10000) return fail();
  return Object.fromEntries(Object.entries(v).map(([key, value]) => [literal(key, tenant), count(value, tenant)]));
};
const tags = fields({
  total_tags_used: count, unique_tags_count: count,
  top_tags: nullable(list(fields({ tag_name: literal, count, percentage: percent, trend: number }))),
  daily_tag_usage: nullable(list(fields({ date: day, tag_counts: nullable(tagCounts) }))),
});
const csat = fields({
  ...numeric(['classified_responses', 'unknown_scale_responses', 'total_surveys_sent', 'total_responses']),
  scale_status: literal,
  scale_groups: list(fields({ scale_max: count, total_responses: count, average: ratingAverage, distribution: list(bucket) })),
  response_rate: percent, avg_rating: ratingAverage, satisfaction_rate: nullable(percent),
  nps: nullable((v, tenant) => { const n = number(v, tenant); return n >= -100 && n <= 100 ? n : fail(); }),
  rating_distribution: list(bucket),
  by_channel: nullable(list(fields({ channel_id: uuid, channel_name: literal, provider_type: literal, avg_rating: ratingAverage, response_count: count, satisfaction_rate: nullable(percent) }))),
  by_agent: nullable(list(fields({ user_id: uuid, user_name: literal, avg_rating: ratingAverage, response_count: count, satisfaction_rate: nullable(percent) }))),
  daily_trend: nullable(list(fields({ date: day, avg_rating: ratingAverage, response_count: count, satisfaction_rate: nullable(percent) }))),
});
const equal = (a: number, b: number) => Math.abs(a - b) <= 1e-7;
function validateCsat(row: CsatReport): void {
  const groups = row.scale_groups!, total = row.total_responses!, sent = row.total_surveys_sent!;
  if (row.scale_status !== 'source_preserved' || total > sent || new Set(groups.map(g => g.scale_max)).size !== groups.length) return fail();
  let responses = 0, classified = 0, unknown = 0, sum = 0, satisfied = 0;
  const histogram = new Map<number, number>();
  for (const group of groups) {
    if (![0, 5, 10].includes(group.scale_max) || group.total_responses === 0 || new Set(group.distribution.map(b => b.rating)).size !== group.distribution.length) return fail();
    let groupCount = 0, groupSum = 0;
    for (const b of group.distribution) {
      if (!b.count || group.scale_max !== 0 && b.rating > group.scale_max) return fail();
      groupCount += b.count; groupSum += b.rating * b.count;
      histogram.set(b.rating, (histogram.get(b.rating) ?? 0) + b.count);
      if (group.scale_max !== 0 && b.rating / group.scale_max >= 0.8) satisfied += b.count;
    }
    if (!Number.isSafeInteger(groupSum) || !Number.isSafeInteger(groupCount) || groupCount !== group.total_responses || !equal(group.average, groupSum / groupCount)) return fail();
    responses += groupCount; sum += groupSum;
    if (group.scale_max === 0) unknown += groupCount; else classified += groupCount;
  }
  if (!Number.isSafeInteger(sum) || !Number.isSafeInteger(responses) || responses !== total || classified !== row.classified_responses || unknown !== row.unknown_scale_responses ||
    !equal(row.avg_rating!, total ? sum / total : 0) || !equal(row.response_rate!, sent ? total / sent * 100 : 0) ||
    (classified === 0 ? row.satisfaction_rate !== null : typeof row.satisfaction_rate !== 'number' || !equal(row.satisfaction_rate, satisfied / classified * 100))) return fail();
  const raw = row.rating_distribution!;
  if (raw.length !== histogram.size || new Set(raw.map(b => b.rating)).size !== raw.length || raw.some(b => b.count !== histogram.get(b.rating!))) return fail();
}
const readers = { overview, inbox, sla, tags, csat };

/** Only native consumed fields enter cache. Definitions/inner metadata/private prose are discarded. */
export function readReport<K extends ReportKind>(value: unknown, tenant: string, kind: K, filter: ReportFilter = {}): ReportRead<ReportData[K]> {
  if (!reportUUID(tenant) || !object(value) || value.success !== true || value.error != null || !object(value.meta)) return fail();
  const meta = value.meta;
  if (meta.tenant_id !== tenant || meta.report !== kind || meta.timezone !== 'UTC' || meta.universe !== 'authenticated tenant' || meta.time_unit !== 'minutes' || meta.percentage_unit !== 'percent_0_100') return fail();
  const start = utc(meta.start_date, tenant), end = utc(meta.end_date, tenant), updated = utc(meta.updated_at, tenant);
  if (Date.parse(start) > Date.parse(end) || filter.from !== undefined && start !== filter.from + 'T00:00:00Z' || filter.to !== undefined && end !== filter.to + 'T23:59:59.999999999Z') return fail();
  let data: ReportData[K];
  if (kind === 'agents') {
    const summary = fields({ total_agents: count, active_agents: count, avg_conversations_per_agent: positive, agents: nullable(list(agent)) })(value.data, tenant);
    data = (summary.agents ?? []) as ReportData[K];
  } else if (kind === 'channels') {
    const summary = fields({ total_channels: count, channels: nullable(list(channel)) })(value.data, tenant);
    data = (summary.channels ?? []) as ReportData[K];
  } else {
    const row = readers[kind as keyof typeof readers](value.data, tenant);
    if (kind === 'csat') validateCsat(row as CsatReport);
    if (kind === 'overview') {
      if (!['empty', 'verified', 'unknown_or_mixed'].includes(String(row.csat_scale_status)) ||
        (row.csat_scale_status === 'verified' ? ![5, 10].includes(Number(row.csat_scale_max)) || Number(row.csat_count) === 0 || Number(row.avg_csat_rating) < 1 || Number(row.avg_csat_rating) > Number(row.csat_scale_max) : row.csat_scale_max !== null) ||
        (row.csat_scale_status === 'empty') !== (row.csat_count === 0)) return fail();
    }
    data = row as ReportData[K];
  }
  return { data, meta: { tenant_id: tenant, report: kind, start_date: start, end_date: end, updated_at: updated, timezone: 'UTC', universe: 'authenticated tenant', time_unit: 'minutes', percentage_unit: 'percent_0_100' } };
}

/** Bots have a distinct owned all-time receipt and seconds; no Reports date interval. */
export function readReportBots(value: unknown, tenant: string): BotStatsRead {
  try {
    if (!reportUUID(tenant) || !object(value) || value.success !== true || value.error != null || !object(value.meta)) return fail();
    const meta = value.meta;
    if (meta.tenant_id !== tenant || meta.scope !== 'tenant_all_time' || meta.time_unit !== 'seconds' || meta.percentage_unit !== 'percent_0_100') return fail();
    const updated = utc(meta.updated_at, tenant);
    const row = fields({ ...numeric(['total_sessions', 'active_sessions', 'completed_sessions', 'handoff_sessions', 'expired_sessions', 'error_sessions']), avg_duration_seconds: positive, completion_rate: percent })(value.data, tenant);
    const total = Number(row.total_sessions), classified = ['active_sessions', 'completed_sessions', 'handoff_sessions', 'expired_sessions', 'error_sessions'].reduce((sum, key) => sum + Number(row[key]), 0);
    if (!Number.isSafeInteger(classified) || classified > total || !equal(Number(row.completion_rate), total ? Number(row.completed_sessions) / total * 100 : 0)) return fail();
    return { data: row as BotsStats, meta: { tenant_id: tenant, scope: 'tenant_all_time', updated_at: updated, time_unit: 'seconds', percentage_unit: 'percent_0_100' } };
  } catch {
    throw new BotStatsReadError('invalid_result');
  }
}
export function botStatsProblem(value: unknown, status: number): BotStatsReadError {
  const errors = { unauthorized: [401, 'unauthorized'], unavailable: [503, 'stats_unavailable'], read_failed: [500, 'stats_failed'], invalid_result: [500, 'stats_failed'] } as const;
  if (object(value) && value.may_have_changed === false) for (const [key, [expected, alias]] of Object.entries(errors)) {
    if (status === expected && value.code === 'bot_stats_' + key && value.error === alias) return new BotStatsReadError(key, status);
  }
  return new BotStatsReadError('read_failed', status);
}
const problems = {
  unauthorized: [401, 'unauthorized'], invalid_filter: [400, 'invalid_filter'], unsupported_filter: [400, 'unsupported_filter'],
  unavailable: [503, 'report_unavailable'], read_failed: [500, 'report_failed'], invalid_result: [500, 'report_failed'],
  invalid_export_type: [400, 'invalid_report_type'], invalid_export_format: [400, 'invalid_format'], export_failed: [500, 'export_failed'],
} as const;
export function reportProblem(value: unknown, status: number): ReportReadError {
  if (object(value) && value.may_have_changed === false) for (const [key, [expected, alias]] of Object.entries(problems)) {
    if (status === expected && value.code === 'reports_' + key && value.error === alias) return new ReportReadError('error.' + key, status);
  }
  return new ReportReadError('error.read_failed', status);
}
